import { ApiErrorSchema } from '@fundline/shared/schemas/members';
import type { z } from 'zod';
import { env } from './env.ts';
import { supabase } from './supabase.ts';

/** An error from the worker API, with its stable code and a message for people. */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
  }
}

/** Sends one request to the worker's /api with the user's access token; errors become ApiRequestError. */
async function workerFetch(
  path: string,
  init: { method: 'GET' | 'POST'; headers?: Record<string, string>; body?: BodyInit },
): Promise<Response> {
  const { data } = await supabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiRequestError('unauthorized', 'Sign in again to continue.', 401);

  let res: Response;
  try {
    res = await fetch(`${env().VITE_WORKER_URL}/api${path}`, {
      method: init.method,
      headers: { Authorization: `Bearer ${token}`, ...init.headers },
      body: init.body,
    });
  } catch {
    throw new ApiRequestError(
      'network_error',
      'Cannot reach the Fundline server. Check your connection and try again.',
      0,
    );
  }
  if (!res.ok) {
    const json: unknown = await res.json().catch(() => null);
    const err = ApiErrorSchema.safeParse(json);
    throw err.success
      ? new ApiRequestError(err.data.error, err.data.message, res.status)
      : new ApiRequestError(
          'http_error',
          `The server answered ${res.status}. Try again.`,
          res.status,
        );
  }
  return res;
}

function parseAnswer<T extends z.ZodType>(json: unknown, schema: T, status: number): z.infer<T> {
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiRequestError('bad_response', 'The server sent an unexpected answer.', status);
  }
  return parsed.data;
}

/** Uploads a file as the raw request body (CSV imports); the answer is validated like workerApi. */
export async function workerUpload<T extends z.ZodType>(
  path: string,
  file: File,
  schema: T,
): Promise<z.infer<T>> {
  const res = await workerFetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/csv',
      'X-File-Name': encodeURIComponent(file.name),
    },
    body: file,
  });
  return parseAnswer(await res.json().catch(() => null), schema, res.status);
}

/** Downloads a file from the worker and hands it to the browser (reports, original files). */
export async function workerDownload(path: string, fallbackName: string): Promise<void> {
  const res = await workerFetch(path, { method: 'GET' });
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Calls the worker's /api with the user's access token and validates the response. */
export async function workerApi<T extends z.ZodType>(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; schema: T },
): Promise<z.infer<T>> {
  const res = await workerFetch(path, {
    method: options.method ?? 'GET',
    headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  return parseAnswer(await res.json().catch(() => null), options.schema, res.status);
}
